import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import QuestionDisplay from './QuestionDisplay';
import { quizService } from '../../services/api';
import { Card, CardContent, Typography, CircularProgress, Box, Button,Paper,Grid } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    borderRadius: 8,
}));
const LoadingContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    '& > p': {
        marginTop: theme.spacing(2),
    },
}));

const QuizStart = () => {
    const { quizId, courseId = 0, ccrId = 0 } = useParams();
    const navigate = useNavigate();

    const [questions, setQuestions] = useState([]);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [endTime, setEndTime] = useState(null);
    const [attempted, setAttempted] = useState(0);
    const [quiz, setQuiz] = useState(null);
    const [userAnswers, setUserAnswers] = useState({});
    const [textAnswers, setTextAnswers] = useState({});
    const [timeLeft, setTimeLeft] = useState(0);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [currentSelectedOptions, setCurrentSelectedOptions] = useState([]);
    const [currentTypedAnswer, setCurrentTypedAnswer] = useState('');
    const [hasTimeLimit, setHasTimeLimit] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showSummary, setShowSummary] = useState(false);

    useEffect(() => {
        const fetchQuizData = async () => {
            try {
                const response = await quizService.getStartQuiz(quizId, courseId, ccrId);
                const questionMap = response.data.question_array || {};
                const questionIds = Object.values(questionMap);

                setQuestions(questionIds);
                const hasLimit = response.data.timeLimit && response.data.timeLimit > 0;
                setHasTimeLimit(hasLimit);

                if (hasLimit) {
                    const serverEndTime = new Date(response.data.endTime).getTime();
                    const fallbackEndTime = Date.now() + (response.data.timeLimit || '') * 60 * 1000;
                    setEndTime(isNaN(serverEndTime) ? fallbackEndTime : serverEndTime);
                }
                setQuiz(response.data);
                
                // Fetch all saved answers for this quiz
                if (response.data.savedAnswers) {
                    // If the API returns saved answers, use them
                    const savedAnswers = response.data.savedAnswers;
                    
                    // Populate userAnswers and textAnswers from saved answers
                    const newUserAnswers = {};
                    const newTextAnswers = {};
                    
                    // Count attempted questions
                    let attemptedCount = 0;
                    
                    Object.keys(savedAnswers).forEach(questionId => {
                        const answerData = savedAnswers[questionId];
                        if (answerData && answerData.answers && answerData.answers.length > 0) {
                            attemptedCount++;
                            
                            // Check if this is a text answer or multiple choice
                            // This is a simplified check - we don't have options here to compare
                            const isLikelyTextAnswer = 
                                answerData.question_type === 'text' || 
                                (typeof answerData.answers[0] === 'string' && 
                                answerData.answers[0].length > 10);
                            
                            if (isLikelyTextAnswer) {
                                // Text answer
                                newTextAnswers[questionId] = answerData.answers[0];
                                newUserAnswers[questionId] = [];
                            } else {
                                // Multiple choice answer
                                newUserAnswers[questionId] = answerData.answers;
                                newTextAnswers[questionId] = '';
                            }
                        }
                    });
                    
                    setUserAnswers(newUserAnswers);
                    setTextAnswers(newTextAnswers);
                    setAttempted(attemptedCount);
                }
            } catch (err) {
                console.error('Failed to load quiz', err);
            }
        };
        fetchQuizData();
    }, [quizId, courseId, ccrId]);


    useEffect(() => {
        if (hasTimeLimit && endTime) {
            const interval = setInterval(() => {
                const timeRemaining = endTime - Date.now();

                if (timeRemaining <= 0) {
                    console.log('Time is up!');
                    clearInterval(interval);
                    autoSubmitQuiz();
                } else {
                    setTimeLeft(timeRemaining);
                }
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [endTime, hasTimeLimit]);
    const saveAnswerToState = async (currentQuestion, selectedOptionIds = [], typedAnswer = '') => {
        const questionId = currentQuestion?.question_details?.question_id;
        if (!questionId) {
            console.warn('Invalid questionId. Skipping save.');
            return;
        }
        const alreadyHadAnswer = userAnswers[questionId]?.length > 0 || textAnswers[questionId];
        const newHasAnswer = (selectedOptionIds?.length > 0) || (typedAnswer?.trim() !== '');

        if (newHasAnswer && !alreadyHadAnswer) {
            setAttempted((prev) => prev + 1);
        } else if (!newHasAnswer && alreadyHadAnswer) {
            setAttempted((prev) => Math.max(prev - 1, 0));
        }

        setUserAnswers((prev) => ({
            ...prev,
            [questionId]: selectedOptionIds,
        }));

        setTextAnswers((prev) => ({
            ...prev,
            [questionId]: typedAnswer,
        }));

        let answerPayload = [];

        if (Array.isArray(selectedOptionIds) && selectedOptionIds.length > 0) {
            answerPayload = selectedOptionIds;
        } else {
            const safeText = typeof typedAnswer === 'string' ? typedAnswer : (typedAnswer ?? '').toString();
            if (safeText && safeText.trim() !== '') {
                answerPayload = [safeText.trim()];
            }
        }

        if (answerPayload.length === 0) return;

        try {
            await quizService.getSavedAnswer({
                question_id: questionId,
                answer: answerPayload,
                assessment_id: quizId,
                qtype: "default_qtype",
                question_cnt: questionIndex,
            });
        } catch (err) {
            console.error("Failed to save answer:", err);
        }
    };
        useEffect(() => {
            let count = 0;
            for (const questionId of questions) {
            const selected = userAnswers[questionId] || [];
            const typed = textAnswers[questionId] || "";

            if ((selected.length > 0) || (typed.trim() !== "")) {
                count++;
            }
            }
            setAttempted(count);
        }, [userAnswers, textAnswers, questions]);


    const goToQuestion = (index) => {
        if (index >= 0 && index < questions.length) {
            setQuestionIndex(index);
        }
    };

    const submitQuiz = async () => {
        try {
            await quizService.submitQuiz({
                assessment_id: quizId,
                course_id: courseId,
                ccr_id: ccrId,
            });

            // Send a message to the parent window that the quiz is completed
            if (window.parent && window.parent !== window) {
            window.parent.postMessage({ 
                type: 'QUIZ_COMPLETED',
                quizId,
                courseId,
                ccrId
            }, '*');
            }

            navigate(`/quiz/result/${quizId}`);
        } catch (err) {
            console.error('Failed to submit quiz', err);
        }
    };

    const autoSubmitQuiz = () => {
        submitQuiz();
    };
    const formatTime = (time) => {
        if (!time || isNaN(time) || time <= 0) return "0:00";
        const totalSeconds = Math.floor(time / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    const recalculateAttemptedCount = () => {
        let count = 0;
        
        // Count questions with selected options
        for (const questionId in userAnswers) {
            if (userAnswers[questionId] && userAnswers[questionId].length > 0) {
                count++;
                continue; // Skip to next question if we already counted this one
            }
            
            // Count questions with text answers
            if (textAnswers[questionId] && textAnswers[questionId].trim() !== '') {
                count++;
            }
        }
        
        setAttempted(count);
    };
    const fetchloadQuestion = async (index) => {
        if (index < 0 || index >= questions.length) {
            // If trying to go beyond the last question, show the summary screen
            if (index === questions.length && !showSummary) {
                // Save the last question's answer first
                if (currentQuestion) {
                    const qid = currentQuestion.question_details?.question_id;
                    if (qid) {
                        const currentSelected = userAnswers[qid] || [];
                        const currentText = textAnswers[qid] || '';
                        await saveAnswerToState(currentQuestion, currentSelected, currentText);
                    }
                }
                recalculateAttemptedCount();
                // Show the summary screen
                setShowSummary(true);
                return;
            }
            return;
        }
        
        // If going back from summary to last question
        if (showSummary && index === questions.length - 1) {
            setShowSummary(false);
        }
        
        // Save current question's answer before loading a new one
        if (currentQuestion) {
            const qid = currentQuestion.question_details?.question_id;
            if (qid) {
                const currentSelected = userAnswers[qid] || [];
                const currentText = textAnswers[qid] || '';
                await saveAnswerToState(currentQuestion, currentSelected, currentText);
            }
        }

        const questionId = questions[index];
        try {
            const res = await quizService.getLoadQuestions({
                quiz_id: quizId,
                course_id: courseId,
                ccr_id: ccrId,
                question_ind: index,
                question_id: questionId
            });

            if (res.data) {
                setCurrentQuestion(res.data);
                setQuestionIndex(index);
                
                const newQuestionId = res.data.question_details?.question_id;
                if (!newQuestionId) {
                    console.warn('Question loaded without a valid ID');
                    setCurrentSelectedOptions([]);
                    setCurrentTypedAnswer('');
                    return;
                }
                
                // Check if there are saved answers for this question in the savedAnswers object
                if (res.data.savedAnswers && res.data.savedAnswers[newQuestionId]) {
                    const savedAnswer = res.data.savedAnswers[newQuestionId];
                    
                    if (savedAnswer.answers && savedAnswer.answers.length > 0) {
                        // Get the current options from the response
                        const currentOptions = res.data.options || [];
                        
                        // Check if this is a multiple choice question
                        const isMultipleChoice = currentOptions.length > 0;
                        
                        if (isMultipleChoice) {
                        // For multiple choice, use the option IDs directly
                        // The component will match these IDs to the current option positions
                        const numericAnswers = savedAnswer.answers.map(
                            id => typeof id === 'string' ? Number(id) : id
                        );
                        
                        setCurrentSelectedOptions(numericAnswers);
                        setUserAnswers(prev => ({
                            ...prev,
                            [newQuestionId]: numericAnswers
                        }));
                        setCurrentTypedAnswer('');
                        setTextAnswers(prev => ({
                            ...prev,
                            [newQuestionId]: ''
                        }));
                        } else {
                        // For text answers
                        setCurrentTypedAnswer(savedAnswer.answers[0]);
                        setCurrentSelectedOptions([]);
                        setUserAnswers(prev => ({
                            ...prev,
                            [newQuestionId]: []
                        }));
                        setTextAnswers(prev => ({
                            ...prev,
                            [newQuestionId]: savedAnswer.answers[0]
                        }));
                        }
                    }
                    } else {
                    // No saved answer for this question
                    console.log(`No saved answer found for question ${newQuestionId}`);
                    setCurrentSelectedOptions([]);
                    setCurrentTypedAnswer('');
                }
            }
        } catch (err) {
            console.error("Error loading question:", err);
        }
    };

    const prepareSubmit = async () => {
        if (currentQuestion) {
            const qid = currentQuestion.question_details?.question_id;
            if (qid) {
                // Save the current answer
                await saveAnswerToState(
                    currentQuestion, 
                    userAnswers[qid] || currentSelectedOptions, 
                    textAnswers[qid] || currentTypedAnswer
                );
                
                // Show confirmation
                setShowConfirmation(true);
            }
        }
    };

    useEffect(() => {
        if (questions.length > 0) {
            fetchloadQuestion(0);
        }
    }, [questions]);
  
    return (
        <div>
            {quiz?.userTest ? (
               <div>
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="70vh"
                    sx={{ backgroundColor: '#f5f5f5' }}
                >
                    <Paper
                        elevation={4}
                        sx={{
                            p: 4,
                            borderRadius: 3,
                            textAlign: 'center',
                            backgroundColor: '#fff',
                        }}
                    >
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            You already attempted this quiz.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate(`/quiz/result/${quizId}`)}
                            sx={{
                                mt: 2,
                                px: 4,
                                py: 1,
                                fontSize: '1rem',
                                backgroundColor: '#2563eb',
                                '&:hover': {
                                    backgroundColor: '#c10d50',
                                },
                                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
                            }}
                        >
                            View Result
                        </Button>
                    </Paper>
                </Box>
            </div>
            ) : (questions && questions.length > 0) ? (
                <div>
                    
                    <Box  
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                        px={1}
                        py={1}
                        sx={{
                            backgroundColor: '#f5f5f5',
                            borderRadius: 1,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        }}
                    >
                        <Typography variant="body1">
                            {attempted} of {questions.length} answered
                        </Typography>

                        {hasTimeLimit && endTime && (
                            <Box display="flex" alignItems="center">
                                <AccessTimeIcon sx={{ mr: 1 }} />
                                <Typography id="quiz-timer" variant="body1">
                                    {formatTime(timeLeft)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    {currentQuestion ? (
                        <Grid container justifyContent="center">
                            <Grid item xs={12} md={8} lg={7}>
                                {/* Navigation Buttons BELOW the Card */}
                                {showSummary ? (
                                    // Summary/confirmation screen
                                    <StyledCard>
                                        <CardContent
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                flexDirection: 'column',
                                                minHeight: '200px',
                                                padding: '24px',
                                            }}
                                        >
                                            <Typography variant="h5" gutterBottom>
                                                Quiz Summary
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 3 }}>
                                                You have answered {attempted} of {questions.length} questions.
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic', color: 'text.secondary' }}>
                                                Note: Once submitted, the answers cannot be changed or edited.
                                            </Typography>
                                            
                                            <Box mt={3} display="flex" justifyContent="center" gap={2}>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => fetchloadQuestion(questionIndex)}
                                                    sx={{
                                                        backgroundColor: '#E0115F',
                                                        '&:hover': {
                                                            backgroundColor: '#c10d50',
                                                        },
                                                        mr: 3,
                                                    }}
                                                >
                                                    ⬅ Previous
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    onClick={submitQuiz}
                                                    sx={{
                                                        backgroundColor: '#E0115F',
                                                        '&:hover': {
                                                            backgroundColor: '#c10d50',
                                                        },
                                                    }}
                                                >
                                                    Finish Quiz
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </StyledCard>
                                ) : (
                                    // Regular question display
                                    <>
                                        <StyledCard>
                                            <CardContent
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    flexDirection: 'column',
                                                    minHeight: '200px',
                                                    padding: '24px',
                                                }}
                                            >
                                                <QuestionDisplay
                                                    questionDetails={currentQuestion}
                                                    options={currentQuestion.options}
                                                    questionIndex={questionIndex}
                                                    totalQuestions={questions.length}
                                                    savedAnswers={userAnswers[currentQuestion.question_details?.question_id] || []}
                                                    userAnswer={userAnswers[currentQuestion.question_details?.question_id] || []}
                                                    userAnswerText={textAnswers[currentQuestion.question_details?.question_id] || ''}
                                                    isCorrectAnswerCount={currentQuestion.correct_answer_count}
                                                    onAnswerChange={(selectedOptions, typedAnswer) => {
                                                        // This function receives both selectedOptions and typedAnswer
                                                        console.log("Answer changed:", selectedOptions, typedAnswer);
                                                        
                                                        const qid = currentQuestion.question_details?.question_id;
                                                        if (!qid) return;
                                                        
                                                        // Update both state variables
                                                        setCurrentSelectedOptions(selectedOptions);
                                                        setCurrentTypedAnswer(typedAnswer);
                                                        
                                                        // Update the stored answers
                                                        setUserAnswers(prev => ({
                                                        ...prev,
                                                        [qid]: selectedOptions
                                                        }));
                                                        
                                                        setTextAnswers(prev => ({
                                                        ...prev,
                                                        [qid]: typedAnswer
                                                        }));
                                                    }}
                                                />
                                            </CardContent>
                                        </StyledCard>

                                        {/* Navigation Buttons */}
                                        <Box mt={2} display="flex" justifyContent="space-between">
                                            <Box>
                                                {questionIndex > 0 && (
                                                    <Button
                                                        variant="contained"
                                                        onClick={() => fetchloadQuestion(questionIndex - 1)}
                                                        sx={{
                                                            backgroundColor: '#E0115F',
                                                            '&:hover': {
                                                                backgroundColor: '#c10d50',
                                                            },
                                                            mr: 3,
                                                        }}
                                                    >
                                                        ⬅ Previous
                                                    </Button>
                                                )}
                                            </Box>
                                            <Button
                                                variant="contained"
                                                onClick={async () => {
                                                    if (!currentQuestion) return;
                                                    
                                                    // Get the current question ID
                                                    const qid = currentQuestion.question_details?.question_id;
                                                    if (!qid) return;
                                                    
                                                    // Save the current answer
                                                    await saveAnswerToState(
                                                        currentQuestion, 
                                                        userAnswers[qid] || currentSelectedOptions, 
                                                        textAnswers[qid] || currentTypedAnswer
                                                    );
                                                    
                                                    // For the last question, this will trigger the summary screen
                                                    await fetchloadQuestion(questionIndex + 1);
                                                }}
                                                sx={{
                                                    backgroundColor: '#E0115F',
                                                    '&:hover': {
                                                        backgroundColor: '#c10d50',
                                                    },
                                                    mr: 3,
                                                }}
                                            >
                                                Next ➡
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </Grid>
                        </Grid>    
                    ) : (
                        <LoadingContainer>
                            <CircularProgress />
                            <Typography variant="body1">Loading Quiz Questions...</Typography>
                        </LoadingContainer>
                    )}            
                </div>
            ) : (
                <p>No questions assigned to this quiz.</p>
            )}
        </div>
    );
};
export default QuizStart;
